import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useLang } from "../context/LangContext";
import { formatLocation } from "../lib/locationFormat";
import { api, formatApiError } from "../lib/api";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
    Search,
    Building2,
    Calendar,
    ScrollText,
    LogOut,
    ShieldCheck,
    Trash2,
    AlertTriangle,
    Loader2,
    Phone,
    Mail,
    MapPin,
    CreditCard,
    Crown,
    BadgeDollarSign,
    Filter,
    ArrowDownAZ,
    ArrowUpAZ,
    Receipt,
    MessageSquare,
    Megaphone,
    Smartphone,
    Clock,
    Lock,
    LockOpen,
    CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";
import { useUnreadCounts, UnreadBadge } from "../hooks/useUnreadCounts";

function formatJoinedAt(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString("en-GB");
    } catch {
        return iso;
    }
}

function formatDateOnly(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString("en-GB");
    } catch {
        return iso;
    }
}

function SubscriptionBadge({ c, t }) {
    if (c.subscription_is_free) {
        return (
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-bold text-emerald-800 ring-1 ring-emerald-300">
                <Crown className="h-3 w-3" /> {t.subStatusFree}
            </span>
        );
    }
    const st = c.subscription_status;
    if (st === "active") {
        return (
            <span className="inline-flex items-center gap-1 rounded-full bg-sky-100 px-2 py-0.5 text-[10px] font-bold text-sky-800 ring-1 ring-sky-300">
                {t.subStatusActive}
            </span>
        );
    }
    if (st === "grace") {
        return (
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-800 ring-1 ring-amber-300">
                {t.subStatusGrace}
            </span>
        );
    }
    if (st === "expired") {
        return (
            <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-[10px] font-bold text-red-800 ring-1 ring-red-300">
                {t.subStatusExpired}
            </span>
        );
    }
    return (
        <span className="inline-flex items-center gap-1 rounded-full bg-neutral-100 px-2 py-0.5 text-[10px] font-bold text-neutral-700 ring-1 ring-neutral-300">
            {t.subStatusNone}
        </span>
    );
}

export default function AdminAccounts() {
    const { company, logout } = useAuth();
    const navigate = useNavigate();
    const { t, lang } = useLang();
    const [search, setSearch] = useState("");
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [deleteTarget, setDeleteTarget] = useState(null); // { id, company_name }
    const [deleting, setDeleting] = useState(false);
    const [deletePassword, setDeletePassword] = useState("");
    const [deleteErr, setDeleteErr] = useState("");

    // ── Default prices state ──
    const [defaultsOpen, setDefaultsOpen] = useState(false);
    const [defaultsMonthly, setDefaultsMonthly] = useState("");
    const [defaultsYearly, setDefaultsYearly] = useState("");
    const [defaultsSaving, setDefaultsSaving] = useState(false);

    // ── Filter + sort (#6) ──
    const [filter, setFilter] = useState("all"); // all | active | expired | grace
    const [sortDir, setSortDir] = useState("none"); // none | asc | desc

    const load = async (q) => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/accounts", { params: q ? { search: q } : {} });
            setItems(Array.isArray(data) ? data : []);
        } catch {
            setItems([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load("");
    }, []);

    useEffect(() => {
        const t = setTimeout(() => load(search), 250);
        return () => clearTimeout(t);
    }, [search]);

    const handleLogout = async () => {
        await logout();
        navigate("/login", { replace: true });
    };

    const performDelete = async () => {
        if (!deleteTarget) return;
        setDeleteErr("");
        if (!deletePassword.trim()) {
            setDeleteErr(t.deletePasswordRequired);
            return;
        }
        setDeleting(true);
        try {
            await api.delete(`/admin/accounts/${deleteTarget.id}`, {
                data: { password: deletePassword },
            });
            setItems((prev) => prev.filter((x) => x.id !== deleteTarget.id));
            toast.success(t.adminAccountDeletedToast);
            setDeleteTarget(null);
            setDeletePassword("");
        } catch (e) {
            const msg = formatApiError(e.response?.data?.detail) || t.adminAccountDeleteFail;
            setDeleteErr(msg);
        } finally {
            setDeleting(false);
        }
    };

    /** Apply local filter + sort to the loaded items list. */
    const visibleItems = useMemo(() => {
        let list = items.slice();
        if (filter !== "all") {
            list = list.filter((c) => {
                if (filter === "active") return c.subscription_status === "active" || c.subscription_status === "free";
                return c.subscription_status === filter;
            });
        }
        if (sortDir !== "none") {
            list.sort((a, b) => {
                const na = (a.company_name || "").trim();
                const nb = (b.company_name || "").trim();
                return sortDir === "asc" ? na.localeCompare(nb, "ar") : nb.localeCompare(na, "ar");
            });
        }
        return list;
    }, [items, filter, sortDir]);

    const openDefaultsDialog = async () => {
        setDefaultsOpen(true);
        try {
            const { data } = await api.get("/admin/subscription-defaults");
            setDefaultsMonthly(String(data?.monthly_price ?? ""));
            setDefaultsYearly(String(data?.yearly_price ?? ""));
        } catch {
            setDefaultsMonthly("");
            setDefaultsYearly("");
        }
    };

    const saveDefaults = async () => {
        setDefaultsSaving(true);
        try {
            await api.post("/admin/subscription-defaults", {
                monthly_price: parseFloat(defaultsMonthly || "0"),
                yearly_price: parseFloat(defaultsYearly || "0"),
            });
            toast.success(t.subDefaultsSaved);
            setDefaultsOpen(false);
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || t.subOverrideFail);
        } finally {
            setDefaultsSaving(false);
        }
    };

    return (
        <div className="min-h-screen app-bg">
            <header className="sticky top-0 z-30 border-b border-neutral-200/70 bg-white/85 backdrop-blur">
                <div className="mx-auto flex h-14 max-w-7xl items-center justify-between gap-2 px-3 sm:h-16 sm:px-6 lg:px-8">
                    <div className="flex min-w-0 items-center gap-2 sm:gap-2.5">
                        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-neutral-900 text-white sm:h-9 sm:w-9 sm:rounded-xl">
                            <ScrollText className="h-4 w-4 sm:h-5 sm:w-5" />
                        </div>
                        <div className="min-w-0">
                            <div className="truncate font-heading text-sm font-bold leading-none sm:text-base">{t.brand}</div>
                            <div className="flex items-center gap-1 truncate text-[11px] text-neutral-500 sm:text-xs">
                                <ShieldCheck className="h-3 w-3 text-amber-700" />
                                {company?.company_name}
                            </div>
                        </div>
                    </div>

                    <div className="flex items-center gap-1">
                    <AdminTopbarNav navigate={navigate} />
                    <Button
                        variant="ghost"
                        onClick={() => navigate("/admin/invoices")}
                        className="h-9 px-2.5 text-neutral-700 hover:bg-amber-50 hover:text-amber-800 sm:px-3"
                        data-testid="admin-invoices-btn"
                    >
                        <Receipt className="h-4 w-4 sm:ms-1" />
                        <span className="hidden sm:inline">{t.invoicesNavBtn}</span>
                    </Button>
                    <Button
                        variant="ghost"
                        onClick={openDefaultsDialog}
                        className="h-9 px-2.5 text-neutral-700 hover:bg-amber-50 hover:text-amber-800 sm:px-3"
                        data-testid="admin-defaults-btn"
                    >
                        <BadgeDollarSign className="h-4 w-4 sm:ms-1" />
                        <span className="hidden sm:inline">{t.subDefaultsBtn}</span>
                    </Button>
                    <Button
                        variant="ghost"
                        onClick={handleLogout}
                        className="h-9 px-2.5 text-neutral-700 hover:bg-red-50 hover:text-red-700 sm:px-3"
                        data-testid="admin-logout-btn"
                    >
                        <LogOut className="h-4 w-4 sm:ms-1" />
                        <span className="hidden sm:inline">{t.navLogout}</span>
                    </Button>
                    </div>
                </div>
            </header>

            <main className="mx-auto max-w-7xl px-3 py-4 sm:px-6 sm:py-6 lg:px-8">
                <div className="mb-4 sm:mb-6">
                    <p className="text-[11px] font-semibold uppercase tracking-wider text-amber-700 sm:text-sm">{t.ownerPanelKicker}</p>
                    <h1 className="font-heading text-2xl font-bold sm:text-3xl lg:text-4xl">{t.accountsLogTitle}</h1>
                    <p className="mt-1 text-xs text-neutral-500 sm:text-sm">
                        {t.accountsLogSubtitle}
                    </p>
                </div>

                <div className="mb-3 flex flex-wrap items-center gap-2" data-testid="admin-filters">
                    <Filter className="h-4 w-4 text-neutral-400" />
                    {[
                        ["all", t.filterAll],
                        ["active", t.subStatusActive],
                        ["grace", t.subStatusGrace],
                        ["expired", t.subStatusExpired],
                    ].map(([k, label]) => (
                        <button
                            key={k}
                            type="button"
                            onClick={() => setFilter(k)}
                            className={`rounded-full px-3 py-1 text-xs font-semibold transition ${
                                filter === k
                                    ? "bg-neutral-900 text-white"
                                    : "border border-neutral-200 bg-white text-neutral-700 hover:bg-neutral-50"
                            }`}
                            data-testid={`admin-filter-${k}`}
                        >
                            {label}
                        </button>
                    ))}
                    <span className="mx-2 hidden h-4 w-px bg-neutral-200 sm:block" />
                    <button
                        type="button"
                        onClick={() => setSortDir(sortDir === "asc" ? "none" : "asc")}
                        className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold transition ${
                            sortDir === "asc"
                                ? "bg-amber-700 text-white"
                                : "border border-neutral-200 bg-white text-neutral-700 hover:bg-neutral-50"
                        }`}
                        data-testid="admin-sort-asc"
                    >
                        <ArrowDownAZ className="h-3.5 w-3.5" />
                        {t.sortAtoZ}
                    </button>
                    <button
                        type="button"
                        onClick={() => setSortDir(sortDir === "desc" ? "none" : "desc")}
                        className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold transition ${
                            sortDir === "desc"
                                ? "bg-amber-700 text-white"
                                : "border border-neutral-200 bg-white text-neutral-700 hover:bg-neutral-50"
                        }`}
                        data-testid="admin-sort-desc"
                    >
                        <ArrowUpAZ className="h-3.5 w-3.5" />
                        {t.sortZtoA}
                    </button>
                </div>

                <div className="mb-6 flex items-center gap-3">
                    <div className="relative w-full max-w-md">
                        <Search className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
                        <Input
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder={t.searchCompanyPlaceholder}
                            className="h-11 pe-10 ps-3"
                            data-testid="admin-accounts-search"
                        />
                    </div>
                    <div className="hidden items-center gap-1 rounded-full bg-neutral-900 px-3 py-1.5 text-xs font-semibold text-white sm:inline-flex">
                        <Building2 className="h-3.5 w-3.5" />
                        <span data-testid="admin-accounts-count">{items.length}</span>
                        <span>{t.companiesWord}</span>
                    </div>
                </div>

                {loading ? (
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                        {Array.from({ length: 6 }).map((_, i) => (
                            <div key={i} className="h-24 animate-pulse rounded-2xl bg-neutral-200/60" />
                        ))}
                    </div>
                ) : items.length === 0 ? (
                    <div className="rounded-2xl border border-dashed border-neutral-300 bg-white/60 p-12 text-center" data-testid="admin-accounts-empty">
                        <Building2 className="mx-auto mb-3 h-8 w-8 text-neutral-400" />
                        <h3 className="font-heading text-lg font-bold">{t.noMatchingAccounts}</h3>
                        <p className="mt-1 text-sm text-neutral-500">{t.noMatchingHint}</p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="admin-accounts-list">
                        {visibleItems.map((c) => {
                            const isExpired = c.subscription_status === "expired";
                            return (
                            <div
                                key={c.id}
                                className={`group flex flex-col gap-3 rounded-2xl border bg-white p-5 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md ${
                                    isExpired
                                        ? "border-red-300 bg-red-50/30 ring-1 ring-red-200"
                                        : "border-neutral-200"
                                }`}
                                data-testid={`admin-account-${c.id}`}
                            >
                                <div className="flex items-start gap-3">
                                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-amber-50 text-amber-700 ring-1 ring-amber-200">
                                        <Building2 className="h-5 w-5" />
                                    </div>
                                    <div className="min-w-0 flex-1">
                                        <h3
                                            className="truncate font-heading text-base font-bold leading-tight"
                                            data-testid={`admin-account-name-${c.id}`}
                                            title={c.company_name}
                                        >
                                            {c.company_name}
                                        </h3>
                                        <div
                                            className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-neutral-500"
                                            data-testid={`admin-account-joined-${c.id}`}
                                        >
                                            <Calendar className="h-3 w-3" />
                                            <span>{t.joinedAt} {formatJoinedAt(c.created_at)}</span>
                                            <SubscriptionBadge c={c} t={t} />
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => setDeleteTarget({ id: c.id, company_name: c.company_name })}
                                        className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-neutral-400 opacity-60 transition hover:bg-red-50 hover:text-red-600 hover:opacity-100 focus-visible:opacity-100 group-hover:opacity-100"
                                        data-testid={`admin-account-delete-btn-${c.id}`}
                                        aria-label={`${t.deleteAccount} ${c.company_name}`}
                                        title={t.deleteAccount}
                                    >
                                        <Trash2 className="h-4 w-4" />
                                    </button>
                                </div>
                                <AccountQuickActions c={c} onChanged={() => load(search)} />
                                <dl className="grid grid-cols-1 gap-1.5 border-t border-neutral-100 pt-3 text-xs">
                                    <div className="flex items-center gap-2">
                                        <Mail className="h-3.5 w-3.5 text-neutral-400" />
                                        <dt className="font-medium text-neutral-500">{t.emailLine}</dt>
                                        <dd
                                            className="ltr-text break-all font-numbers font-semibold text-neutral-800"
                                            dir="ltr"
                                            data-testid={`admin-account-email-${c.id}`}
                                        >
                                            {c.email || t.notSet}
                                        </dd>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <MapPin className="h-3.5 w-3.5 text-neutral-400" />
                                        <dt className="font-medium text-neutral-500">{t.locationLine}</dt>
                                        <dd
                                            className="truncate font-semibold text-neutral-800"
                                            data-testid={`admin-account-location-${c.id}`}
                                        >
                                            {c.country && c.city ? formatLocation(c.country, c.city, c.country_code, lang) : t.notSet}
                                        </dd>
                                    </div>
                                    {c.account_type ? (
                                        <div className="flex items-center gap-2">
                                            <ShieldCheck className="h-3.5 w-3.5 text-neutral-400" />
                                            <dt className="font-medium text-neutral-500">{t.accountTypeTitle}:</dt>
                                            <dd className="font-semibold text-neutral-800" data-testid={`admin-account-type-${c.id}`}>
                                                {c.account_type === "manager" ? t.accountTypeManager : t.accountTypeIndividual}
                                            </dd>
                                            {c.account_type === "manager" && c.company_code ? (
                                                <dd className="ms-auto rounded bg-amber-50 px-1.5 py-0.5 font-numbers text-[11px] font-bold tracking-wider text-amber-800 ring-1 ring-amber-200" dir="ltr">
                                                    {c.company_code}
                                                </dd>
                                            ) : null}
                                        </div>
                                    ) : null}
                                </dl>
                                {c.account_type === "manager" ? (
                                    <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50/40 p-2.5" data-testid={`admin-account-employees-${c.id}`}>
                                        <div className="flex items-center justify-between">
                                            <span className="text-[10px] font-bold uppercase tracking-wider text-amber-700">
                                                {t.employeeRecordsTitle}
                                            </span>
                                            <span className="font-numbers text-sm font-bold text-neutral-800">
                                                {c.employees_count || 0}/{c.employee_limit || 0}
                                            </span>
                                        </div>
                                        {c.employees && c.employees.length > 0 ? (
                                            <ul className="mt-1.5 flex flex-wrap gap-1">
                                                {c.employees.map((e) => (
                                                    <li
                                                        key={e.id}
                                                        className="rounded-full bg-white px-2 py-0.5 text-xs font-medium text-neutral-700 ring-1 ring-neutral-200"
                                                        data-testid={`admin-employee-chip-${e.id}`}
                                                    >
                                                        {e.company_name}
                                                    </li>
                                                ))}
                                            </ul>
                                        ) : (
                                            <p className="mt-1 text-[11px] text-neutral-500">{t.employeeRecordsEmpty}</p>
                                        )}
                                    </div>
                                ) : null}
                                {c.account_type !== "employee" ? (
                                    <div
                                        className="mt-1 rounded-lg border border-neutral-200 bg-neutral-50/60 p-2.5 text-xs"
                                        data-testid={`admin-account-sub-${c.id}`}
                                    >
                                        <div className="mb-1 flex items-center justify-between">
                                            <span className="text-[10px] font-bold uppercase tracking-wider text-neutral-500">
                                                <CreditCard className="me-1 inline h-3 w-3" />
                                                {t.subPlanLabel}
                                            </span>
                                        </div>
                                        {c.subscription_is_free ? (
                                            <div className="font-semibold text-emerald-700">
                                                <Crown className="me-1 inline h-3.5 w-3.5" />
                                                {t.subStatusFree}
                                            </div>
                                        ) : (
                                            <dl className="grid grid-cols-1 gap-0.5 text-[11px]">
                                                <div className="flex justify-between">
                                                    <dt className="text-neutral-500">{t.subPlanLabel}</dt>
                                                    <dd className="font-semibold text-neutral-800">
                                                        {c.subscription_plan === "monthly"
                                                            ? t.subPlanMonthly
                                                            : c.subscription_plan === "yearly"
                                                            ? t.subPlanYearly
                                                            : t.subPlanNone}
                                                    </dd>
                                                </div>
                                                <div className="flex justify-between">
                                                    <dt className="text-neutral-500">{t.subStartLabel}</dt>
                                                    <dd
                                                        className="font-numbers font-semibold text-neutral-800"
                                                        dir="ltr"
                                                        data-testid={`admin-account-sub-start-${c.id}`}
                                                    >
                                                        {formatDateOnly(c.subscription_start_date)}
                                                    </dd>
                                                </div>
                                                <div className="flex justify-between">
                                                    <dt className="text-neutral-500">{t.subExpiryLabel}</dt>
                                                    <dd
                                                        className={`font-numbers font-semibold ${
                                                            isExpired
                                                                ? "text-red-700"
                                                                : c.subscription_status === "grace"
                                                                ? "text-amber-700"
                                                                : "text-neutral-800"
                                                        }`}
                                                        dir="ltr"
                                                        data-testid={`admin-account-sub-expiry-${c.id}`}
                                                    >
                                                        {formatDateOnly(c.subscription_expiry_date)}
                                                    </dd>
                                                </div>
                                                <div className="flex justify-between">
                                                    <dt className="text-neutral-500">{t.subPriceLabel}</dt>
                                                    <dd
                                                        className="font-numbers font-semibold text-neutral-800"
                                                        dir="ltr"
                                                        data-testid={`admin-account-sub-price-${c.id}`}
                                                    >
                                                        {c.subscription_price !== null && c.subscription_price !== undefined
                                                            ? `${c.subscription_price} ${c.subscription_currency || "ر.س"}`
                                                            : "—"}
                                                    </dd>
                                                </div>
                                                {/* Invoice stats — total / new / renewal counts */}
                                                {(c.invoices_total || 0) > 0 ? (
                                                    <div
                                                        className="mt-1 flex flex-wrap items-center gap-1.5 border-t border-neutral-100 pt-2 text-[10px]"
                                                        data-testid={`admin-account-invoice-stats-${c.id}`}
                                                    >
                                                        <span className="inline-flex items-center gap-1 rounded-full bg-neutral-100 px-2 py-0.5 font-bold text-neutral-700">
                                                            {t.statsTotalSubs}: <span className="font-numbers">{c.invoices_total}</span>
                                                        </span>
                                                        {c.invoices_new ? (
                                                            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 font-bold text-emerald-700 ring-1 ring-emerald-200">
                                                                {t.statsNewSubs}: <span className="font-numbers">{c.invoices_new}</span>
                                                            </span>
                                                        ) : null}
                                                        {c.invoices_renewal ? (
                                                            <span className="inline-flex items-center gap-1 rounded-full bg-sky-50 px-2 py-0.5 font-bold text-sky-700 ring-1 ring-sky-200">
                                                                {t.statsRenewalSubs}: <span className="font-numbers">{c.invoices_renewal}</span>
                                                            </span>
                                                        ) : null}
                                                    </div>
                                                ) : null}
                                            </dl>
                                        )}
                                    </div>
                                ) : null}
                            </div>
                            );
                        })}
                    </div>
                )}
            </main>

            {/* Default Prices Dialog */}
            <AlertDialog
                open={defaultsOpen}
                onOpenChange={(v) => {
                    if (!v && !defaultsSaving) setDefaultsOpen(false);
                }}
            >
                <AlertDialogContent dir={t.dir} data-testid="admin-defaults-dialog">
                    <AlertDialogHeader className="text-start">
                        <AlertDialogTitle className="flex items-center gap-2 text-amber-800">
                            <BadgeDollarSign className="h-5 w-5" />
                            {t.subDefaultsTitle}
                        </AlertDialogTitle>
                        <AlertDialogDescription>{t.subDefaultsHint}</AlertDialogDescription>
                    </AlertDialogHeader>
                    <div className="space-y-3 py-2">
                        <div>
                            <label className="mb-1.5 block text-xs font-semibold text-neutral-700">
                                {t.subPlanMonthly} — ر.س
                            </label>
                            <Input
                                value={defaultsMonthly}
                                onChange={(e) => setDefaultsMonthly(e.target.value)}
                                inputMode="decimal"
                                placeholder="0"
                                className="h-10 font-numbers"
                                dir="ltr"
                                data-testid="admin-defaults-monthly"
                            />
                        </div>
                        <div>
                            <label className="mb-1.5 block text-xs font-semibold text-neutral-700">
                                {t.subPlanYearly} — ر.س
                            </label>
                            <Input
                                value={defaultsYearly}
                                onChange={(e) => setDefaultsYearly(e.target.value)}
                                inputMode="decimal"
                                placeholder="0"
                                className="h-10 font-numbers"
                                dir="ltr"
                                data-testid="admin-defaults-yearly"
                            />
                        </div>
                    </div>
                    <AlertDialogFooter className="flex-row-reverse gap-2 sm:flex-row-reverse">
                        <AlertDialogCancel disabled={defaultsSaving} data-testid="admin-defaults-cancel">
                            {t.cancel}
                        </AlertDialogCancel>
                        <AlertDialogAction
                            onClick={(e) => {
                                e.preventDefault();
                                saveDefaults();
                            }}
                            disabled={defaultsSaving}
                            className="bg-amber-700 text-white hover:bg-amber-800"
                            data-testid="admin-defaults-save"
                        >
                            {defaultsSaving ? (
                                <Loader2 className="ms-1 h-4 w-4 animate-spin" />
                            ) : (
                                <ShieldCheck className="ms-1 h-4 w-4" />
                            )}
                            {t.save || "حفظ"}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            <AlertDialog
                open={!!deleteTarget}
                onOpenChange={(v) => {
                    if (!v && !deleting) {
                        setDeleteTarget(null);
                        setDeletePassword("");
                        setDeleteErr("");
                    }
                }}
            >
                <AlertDialogContent dir={t.dir} data-testid="admin-account-delete-dialog">
                    <AlertDialogHeader className="text-start">
                        <AlertDialogTitle className="flex items-center gap-2 text-red-800">
                            <AlertTriangle className="h-5 w-5" />
                            {t.deleteAccount}
                        </AlertDialogTitle>
                        <AlertDialogDescription>
                            {t.actionWillDelete} <span className="font-bold text-red-700">{t.irreversible}</span>. {t.sectorsDeleted}
                        </AlertDialogDescription>
                        <div className="space-y-2 text-sm text-neutral-700">
                            <ul className="list-disc space-y-0.5 pe-5">
                                <li>
                                    {t.adminDeleteWillDelete1}{" "}
                                    <span className="font-bold">"{deleteTarget?.company_name}"</span>
                                </li>
                                <li>{t.adminDeleteWillDelete2}</li>
                            </ul>
                            <p>{t.adminDeleteCompanyFreed}</p>
                        </div>
                    </AlertDialogHeader>

                    <div className="space-y-2 py-2">
                        <label className="block text-xs font-semibold text-neutral-700">
                            {t.deletePasswordPrompt}
                        </label>
                        <Input
                            type="password"
                            value={deletePassword}
                            onChange={(e) => {
                                setDeletePassword(e.target.value);
                                if (deleteErr) setDeleteErr("");
                            }}
                            placeholder={t.deletePasswordPlaceholder}
                            className="h-10"
                            autoFocus
                            data-testid="admin-account-delete-password"
                        />
                        {deleteErr ? (
                            <p className="text-xs font-semibold text-red-600" data-testid="admin-account-delete-err">
                                {deleteErr}
                            </p>
                        ) : null}
                    </div>
                    <AlertDialogFooter className="flex-row-reverse gap-2 sm:flex-row-reverse">
                        <AlertDialogCancel
                            disabled={deleting}
                            data-testid="admin-account-delete-cancel"
                        >
                            {t.cancel}
                        </AlertDialogCancel>
                        <AlertDialogAction
                            onClick={(e) => {
                                e.preventDefault();
                                performDelete();
                            }}
                            disabled={deleting}
                            className="bg-red-600 text-white hover:bg-red-700"
                            data-testid="admin-account-delete-confirm"
                        >
                            {deleting ? (
                                <Loader2 className="ms-1 h-4 w-4 animate-spin" />
                            ) : (
                                <Trash2 className="ms-1 h-4 w-4" />
                            )}
                            {t.deleteForever}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}

/** Per-account inline controls: pending-payment approval + manual lock/unlock. */
function AccountQuickActions({ c, onChanged }) {
    const [busy, setBusy] = useState(false);

    const pending = !!c.subscription_pending_plan;
    const locked = !!c.is_manually_locked;
    const expired = c.subscription_status === "expired" || c.subscription_status === "none";

    const approvePayment = async () => {
        setBusy(true);
        try {
            await api.post(`/admin/accounts/${c.id}/subscription/approve`);
            toast.success("تم تفعيل الاشتراك");
            onChanged();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "فشل التفعيل");
        } finally { setBusy(false); }
    };

    const toggleLock = async () => {
        setBusy(true);
        try {
            if (locked) {
                await api.post(`/admin/accounts/${c.id}/unlock`);
                toast.success("تم فتح الحساب");
            } else {
                if (!window.confirm("قفل هذا الحساب يدوياً؟")) { setBusy(false); return; }
                await api.post(`/admin/accounts/${c.id}/lock`);
                toast.success("تم إيقاف الحساب");
            }
            onChanged();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "فشل تنفيذ العملية");
        } finally { setBusy(false); }
    };

    if (!pending && !locked && c.account_type !== "employee") {
        return (
            <div className="mt-2 flex justify-end">
                <Button
                    size="sm"
                    variant="ghost"
                    onClick={toggleLock}
                    disabled={busy}
                    className="h-7 gap-1 text-[11px] text-neutral-500 hover:bg-red-50 hover:text-red-700"
                    data-testid={`admin-lock-btn-${c.id}`}
                >
                    <Lock className="h-3.5 w-3.5" />
                    قفل الحساب
                </Button>
            </div>
        );
    }

    return (
        <div className="mt-2 space-y-2">
            {pending ? (
                <div className="flex items-center justify-between gap-2 rounded-lg bg-amber-50 px-2.5 py-2 ring-1 ring-amber-300" data-testid={`admin-pending-row-${c.id}`}>
                    <div className="min-w-0">
                        <div className="text-[11px] font-bold text-amber-900">
                            بانتظار الدفع — {c.subscription_pending_plan === "yearly" ? "سنوية" : "شهرية"}
                        </div>
                        <div className="font-numbers text-[10px] text-amber-700">
                            {c.subscription_pending_price} {c.subscription_pending_currency || "ر.س"}
                        </div>
                    </div>
                    <Button
                        size="sm"
                        onClick={approvePayment}
                        disabled={busy}
                        className="h-7 gap-1 bg-emerald-700 px-2.5 text-[11px] text-white hover:bg-emerald-800"
                        data-testid={`admin-paid-btn-${c.id}`}
                    >
                        <CheckCircle2 className="h-3.5 w-3.5" />
                        تم الدفع
                    </Button>
                </div>
            ) : null}
            {locked ? (
                <div className="flex items-center justify-between gap-2 rounded-lg bg-red-50 px-2.5 py-2 ring-1 ring-red-300">
                    <div className="text-[11px] font-bold text-red-900">موقوف يدوياً من الإدارة</div>
                    <Button
                        size="sm"
                        onClick={toggleLock}
                        disabled={busy || expired}
                        title={expired ? "لا يمكن الفتح — الاشتراك منتهي" : ""}
                        className="h-7 gap-1 bg-emerald-700 px-2.5 text-[11px] text-white hover:bg-emerald-800 disabled:bg-neutral-300"
                        data-testid={`admin-unlock-btn-${c.id}`}
                    >
                        <LockOpen className="h-3.5 w-3.5" />
                        فتح الحساب
                    </Button>
                </div>
            ) : null}
            {!locked && c.account_type !== "employee" ? (
                <div className="flex justify-end">
                    <Button
                        size="sm"
                        variant="ghost"
                        onClick={toggleLock}
                        disabled={busy}
                        className="h-7 gap-1 text-[11px] text-neutral-500 hover:bg-red-50 hover:text-red-700"
                        data-testid={`admin-lock-btn-${c.id}`}
                    >
                        <Lock className="h-3.5 w-3.5" />
                        قفل الحساب
                    </Button>
                </div>
            ) : null}
        </div>
    );
}

/** Shared top-bar nav for admin pages (chat / announcements / devices). */
function AdminTopbarNav({ navigate }) {
    const { chat: chatUnread } = useUnreadCounts();
    return (
        <>
            <Button
                variant="ghost"
                onClick={() => navigate("/admin/chat")}
                className="relative h-9 px-2.5 text-neutral-700 hover:bg-amber-50 hover:text-amber-800 sm:px-3"
                data-testid="admin-chat-nav-btn"
                aria-label="المحادثات"
            >
                <MessageSquare className="h-4 w-4 sm:ms-1" />
                <span className="hidden sm:inline">المحادثات</span>
                <UnreadBadge count={chatUnread} testid="admin-chat-badge" />
            </Button>
            <Button
                variant="ghost"
                onClick={() => navigate("/admin/devices")}
                className="h-9 px-2.5 text-neutral-700 hover:bg-amber-50 hover:text-amber-800 sm:px-3"
                data-testid="admin-devices-nav-btn"
                aria-label="أجهزة الجوال"
            >
                <Smartphone className="h-4 w-4 sm:ms-1" />
                <span className="hidden sm:inline">الأجهزة</span>
            </Button>
            <Button
                variant="ghost"
                onClick={() => navigate("/admin/announcements")}
                className="h-9 px-2.5 text-neutral-700 hover:bg-amber-50 hover:text-amber-800 sm:px-3"
                data-testid="admin-announcements-nav-btn"
                aria-label="ما الجديد"
            >
                <Megaphone className="h-4 w-4 sm:ms-1" />
                <span className="hidden sm:inline">ما الجديد</span>
            </Button>
        </>
    );
}
